type AnyFilterQueryKeys = Record<string, string>;
type SelectedFiltersMap = Map<string, Set<string>>;

const normalizeValue = (value: string) => value.trim().toLowerCase();

const hasFilterData = (value: string | Array<string> | undefined) => {
	if (value == null) {
		return false;
	}

	if (Array.isArray(value)) {
		return value.some((entry) => normalizeValue(entry) !== '');
	}

	return normalizeValue(value) !== '';
};

const matchesSelectedValue = (
	value: string | Array<string>,
	selectedValues: Set<string>,
	normalize: (value: string) => string,
) => {
	if (Array.isArray(value)) {
		const normalizedValues = new Set(value.map(normalize));

		for (const selectedValue of selectedValues) {
			if (normalizedValues.has(normalize(selectedValue))) {
				return true;
			}
		}

		return false;
	}

	const normalizedValue = normalize(value);

	for (const selectedValue of selectedValues) {
		if (normalizedValue === normalize(selectedValue)) {
			return true;
		}
	}

	return false;
};

const mapSelectedValuesToFilterNames = (
	selectedValues: Set<string>,
	options: Array<{ key: string; name: string }> | undefined,
) => {
	if (options == null) {
		return selectedValues;
	}

	const optionNamesByKey = new Map(
		options.map((option) => [normalizeValue(option.key), option.name]),
	);

	return new Set(
		Array.from(
			selectedValues,
			(selectedValue) =>
				optionNamesByKey.get(normalizeValue(selectedValue)) ?? selectedValue,
		),
	);
};

export const matchesSelectedFiltersByQueryKeys = <
	TFilterQueryKeys extends AnyFilterQueryKeys,
	TResult extends Record<string, unknown>,
	TFilters extends Record<string, unknown>,
>({
	result,
	selectedFilters,
	filters,
	filterQueryKeys,
	resultFieldByQueryKey,
	optionsFieldByQueryKey,
	normalizeByQueryKey,
	rejectWhenNoFilterData = false,
}: {
	result: TResult;
	selectedFilters: SelectedFiltersMap;
	filters: TFilters;
	filterQueryKeys: TFilterQueryKeys;
	resultFieldByQueryKey: Record<
		Extract<keyof TFilterQueryKeys, string>,
		keyof TResult
	>;
	optionsFieldByQueryKey?: Partial<
		Record<Extract<keyof TFilterQueryKeys, string>, keyof TFilters>
	>;
	normalizeByQueryKey?: Partial<
		Record<Extract<keyof TFilterQueryKeys, string>, (value: string) => string>
	>;
	rejectWhenNoFilterData?: boolean;
}) => {
	for (const queryKeyRef of Object.keys(filterQueryKeys) as Array<
		Extract<keyof TFilterQueryKeys, string>
	>) {
		const groupKey = filterQueryKeys[queryKeyRef];
		const selectedValues = selectedFilters.get(groupKey);

		if (selectedValues == null || selectedValues.size === 0) {
			continue;
		}

		const resultField = resultFieldByQueryKey[queryKeyRef];
		const resultValue = result[resultField] as
			| string
			| Array<string>
			| undefined;

		if (!hasFilterData(resultValue)) {
			if (rejectWhenNoFilterData) {
				return false;
			}
			continue;
		}

		const optionsField = optionsFieldByQueryKey?.[queryKeyRef];
		const options =
			optionsField == null
				? undefined
				: (filters[optionsField] as Array<{ key: string; name: string }>);

		const selectedNames = mapSelectedValuesToFilterNames(
			selectedValues,
			options,
		);
		const normalize = normalizeByQueryKey?.[queryKeyRef] ?? normalizeValue;

		if (
			!matchesSelectedValue(
				resultValue as string | Array<string>,
				selectedNames,
				normalize,
			)
		) {
			return false;
		}
	}

	return true;
};
